import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Code,
  Coins,
  Cpu,
  ExternalLink,
  Globe,
  Key,
  Layers,
  Zap,
} from 'lucide-react';

const SITE = 'https://resonatia.io';

const FEATURES = [
  {
    icon: Cpu,
    title: 'Edge-Ready by Design',
    text: 'Every model in our network is optimized for speed and size — designed to run on phones, edge devices, and the cloud. Smaller, faster, more efficient — without sacrificing performance.',
  },
  {
    icon: Globe,
    title: 'Built on a Global Mesh',
    text: 'Our models are distributed across a decentralized mesh of devices. Ultra-low latency, high availability, and unstoppable uptime.',
  },
  {
    icon: Coins,
    title: 'Host & Earn With ESSENCE',
    text: 'Add your own AI models to the network and earn rewards whenever they\'re used. You control the pricing. We handle the infrastructure.',
  },
];

const MODELS = [
  {
    name: 'Qwen2.5 3B Instruct',
    slug: 'qwen2-5-3b-instruct-2-5',
    provider: 'Qwen',
    quantization: 'Q4_K_M',
    size: '1.80 GB',
    version: '2.5',
  },
  {
    name: 'Phi-3 Mini 128K Instruct',
    slug: 'phi-3-mini-128k-instruct-3-0',
    provider: 'Microsoft',
    quantization: 'Q4_K_M',
    size: '2.23 GB',
    version: '3.0',
  },
  {
    name: 'Gemma 2B Instruct',
    slug: 'gemma-2b-instruct-1-0',
    provider: 'Google',
    quantization: 'Q4_K_M',
    size: '1.52 GB',
    version: '1.0',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Get Your API Key',
    text: 'Sign up and generate your key in seconds. No credit card required.',
  },
  {
    n: '2',
    title: 'Choose a Model',
    text: 'Browse our catalog of edge-optimized AI models — organized by task and performance.',
  },
  {
    n: '3',
    title: 'Start Building',
    text: 'Use our REST API to start making requests in minutes. Streaming, pay-as-you-go, no limits.',
  },
];

const STATS = [
  { emoji: '🚀', value: '500+', label: 'AI Models' },
  { emoji: '🧑‍💻', value: '2.2K', label: 'Developers' },
  { emoji: '🌐', value: '7K', label: 'Mesh Nodes' },
  { emoji: '📈', value: '20T', label: 'Monthly Tokens' },
];

const FOOTER_LINKS = [
  { label: 'Documentation', href: `${SITE}/docs` },
  { label: 'Constitution', href: `${SITE}/constitution` },
  { label: 'Pricing', href: `${SITE}/pricing` },
  { label: 'Earn with us', href: `${SITE}/earn` },
  { label: 'Console', href: `${SITE}/console` },
];

function GlowLink({
  href,
  children,
  primary = false,
}: {
  href: string;
  children: ReactNode;
  primary?: boolean;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
        primary
          ? 'bg-white text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90'
          : 'border border-white/25 bg-white/5 text-white hover:border-white/35 hover:bg-white/10'
      }`}
    >
      {children}
    </a>
  );
}

export default function ResonatiaPage() {
  return (
    <>
      <Helmet>
        <title>Resonatia — The Fastest, Most Affordable AI API | SELF Labs</title>
        <meta
          name="description"
          content="Resonatia — hundreds of edge-optimized AI models through one unified API. Global mesh, pay-as-you-go, host models and earn ESSENCE."
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 70%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 60%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        {/* Hero */}
        <section className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Zap className="h-3.5 w-3.5" />
                Production Ready · Edge Optimized
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                The Fastest, Most Affordable AI API
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-white/65">
                Access hundreds of high-performance AI models — LLMs, vision, audio, embeddings — all
                through a single, unified API.
              </p>
              <p className="mt-3 text-white/45">Pay only for what you use. No bloat. No barriers.</p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <GlowLink href={SITE} primary>
                  Get Started
                  <ExternalLink className="h-4 w-4" />
                </GlowLink>
                <GlowLink href={`${SITE}/docs`}>
                  View API Docs
                  <ArrowRight className="h-4 w-4" />
                </GlowLink>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7"
              >
                <f.icon className="mb-4 h-8 w-8 text-white/70" strokeWidth={1.5} />
                <h2 className="text-lg font-semibold">{f.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Popular models */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <h2 className="text-3xl font-bold">Popular Models</h2>
              <p className="mx-auto mt-3 max-w-xl text-white/55">
                Models ready to power your apps — from chat to generation to audio understanding.
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {MODELS.map((model, i) => (
                <motion.div
                  key={model.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="font-semibold">{model.name}</h3>
                    <div className="flex gap-1.5">
                      <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/60">
                        free
                      </span>
                      <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/60">
                        mesh
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 font-mono text-xs text-white/40">{model.slug}</p>
                  <dl className="mt-4 space-y-1.5 text-sm">
                    {[
                      ['Provider', model.provider],
                      ['Quantization', model.quantization],
                      ['Size', model.size],
                      ['Version', model.version],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4">
                        <dt className="text-white/45">{k}</dt>
                        <dd className="text-white/75">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <a
                    href={`${SITE}/models`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-block text-sm text-white/70 underline underline-offset-2 hover:text-white"
                  >
                    Use This Model
                  </a>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 text-center">
              <GlowLink href={`${SITE}/models`}>Browse All Models</GlowLink>
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <span className="text-lg">🧩</span>
              <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-white/40">
                Get Started in Minutes
              </p>
              <h2 className="mt-2 text-3xl font-bold">How Resonatia Works</h2>
              <p className="mx-auto mt-3 max-w-xl text-white/55">
                Simple API access to powerful AI models — or host your own and earn rewards.
              </p>
            </div>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/5 text-lg font-bold">
                    {s.n}
                  </div>
                  <h3 className="mt-4 font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-white/55">{s.text}</p>
                </motion.div>
              ))}
            </div>

            <div className="mx-auto mt-12 max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
                <Code className="h-4 w-4 text-white/50" />
                <span className="font-mono text-xs text-white/45">API Example</span>
              </div>
              <pre className="overflow-x-auto p-4 font-mono text-sm leading-relaxed text-white/70">
{`POST /v1/mesh/chat
Authorization: Bearer sk-...

{
  "model": "mesh/llama-3.1-8b",
  "messages": [
    {"role": "user", "content": "Hello!"}
  ],
  "stream": true
}`}
              </pre>
            </div>
            <p className="mt-6 text-center">
              <a
                href={`${SITE}/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/60 underline underline-offset-2 hover:text-white"
              >
                View Full API Docs
              </a>
            </p>
          </div>
        </section>

        {/* Earn */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-2xl border border-white/12 bg-white/[0.03] p-8 text-center sm:p-12"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">Earn With Us</p>
            <Layers className="mx-auto mt-4 h-10 w-10 text-white/60" />
            <h2 className="mt-4 text-2xl font-bold sm:text-3xl">Want to Host Your Own Model?</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/55">
              Submit any compatible AI model. We&apos;ll optimize it and distribute it across the mesh. You earn
              ESSENCE tokens every time it&apos;s used — even from mobile devices.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <GlowLink href={`${SITE}/earn`}>Learn About Hosting</GlowLink>
              <GlowLink href={SITE} primary>
                Get Started
                <ExternalLink className="h-4 w-4" />
              </GlowLink>
            </div>
          </motion.div>
        </section>

        {/* Stats */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">Live Network Stats</p>
            <h2 className="mt-2 text-3xl font-bold">The Network</h2>
            <p className="text-sm text-white/45">Real-Time Statistics</p>
            <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
              {STATS.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                >
                  <span className="text-xl">{stat.emoji}</span>
                  <p className="mt-2 text-2xl font-bold sm:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-white/45 sm:text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold">Resonatia</h2>
            <p className="mt-2 text-white/50">Quantized Models on a Rewarding Mesh</p>
            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3">
              {FOOTER_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/55 hover:text-white"
                >
                  {link.label}
                </a>
              ))}
            </div>
            <p className="mt-10 text-sm text-white/40">
              Part of the SELF Labs ecosystem. For the AuRA middleware story, see{' '}
              <a href="/aura-resonant-layer" className="text-white/70 underline underline-offset-2 hover:text-white">
                AuRA Resonant Layer
              </a>
              .
            </p>
            <div className="mt-8">
              <GlowLink href={SITE} primary>
                Go to resonatia.io
                <Key className="h-4 w-4" />
              </GlowLink>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
